/**
 * MicrobiologyTestingPage — public-facing landing for Sterile Mate × A&B Labs
 * partnership. Routed at /microbiology-testing. Same dark-navy aesthetic as
 * BackorderSourcingPage, SolutionsOverviewPage, and the marketing landing —
 * full-bleed photo hero, glass-morphism cards, amber/cyan accents.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import { Button } from "@/components/ui/button";
import { useQuoteCart } from "@/contexts/QuoteCartContext";
import {
  ArrowLeft,
  ArrowRight,
  Atom,
  Award,
  Beaker,
  Bug,
  Calendar,
  CheckCircle2,
  ChevronRight,
  Clock,
  Download,
  Droplets,
  ExternalLink,
  FileText,
  FlaskConical,
  Mail,
  MapPin,
  Microscope,
  Minus,
  Phone,
  Pipette,
  Plus,
  ShieldCheck,
  ShoppingCart,
  Sparkles,
  TestTube,
  Zap,
} from "lucide-react";

// ============================================================================
// Stock imagery — sourced from Pexels CDN. Every image hand-picked to depict
// actual microbiology / clinical water-testing work (petri dishes, bacterial
// cultures, lab benches with Biobase equipment) — NOT generic hospital
// patient photography.
//   HERO_BG          → 8940350: scientist working with petri dishes + Bunsen
//                      burner on a lab bench (full-bleed background)
//   HERO_CARD        → 4031441: gloved hands holding a petri dish with
//                      live bacterial colonies (the iconic microbiology shot)
//   PARTNERSHIP_PHOTO → 33790175: modern lab interior with Biobase analytical
//                      equipment, for the Sterile Mate × A&B Labs banner
// ============================================================================
const HERO_BG =
  "https://images.pexels.com/photos/8940350/pexels-photo-8940350.jpeg?auto=compress&cs=tinysrgb&dpr=2&fit=crop&w=1920&h=900";
const HERO_CARD =
  "https://images.pexels.com/photos/4031441/pexels-photo-4031441.jpeg?auto=compress&cs=tinysrgb&dpr=2&fit=crop&w=900&h=900";
const PARTNERSHIP_PHOTO =
  "https://images.pexels.com/photos/33790175/pexels-photo-33790175.jpeg?auto=compress&cs=tinysrgb&dpr=2&fit=crop&w=1200&h=520";

// ============================================================================
// CORE WATER / MICROBIOLOGY TESTING SERVICES (top-of-fold quick scan)
// ============================================================================
const SERVICE_PILLARS = [
  {
    icon: Droplets,
    accent: "cyan",
    title: "Healthcare Water Testing",
    points: ["Legionella PCR + culture", "Total coliforms / E. coli", "Pseudomonas aeruginosa", "AAMI TIR34 compliance"],
  },
  {
    icon: Microscope,
    accent: "violet",
    title: "Microbial ID & Screens",
    points: ["Culture identifications (genus)", "Yeast & mold ID screens", "Gram positive organisms", "Mold-specific qPCR (MSQPCR)"],
  },
  {
    icon: TestTube,
    accent: "amber",
    title: "Environmental Surveillance",
    points: ["Surface bio-burden testing", "ATP cleanliness verification", "Pre/post-construction sampling", "Sterile processing audits"],
  },
  {
    icon: Beaker,
    accent: "emerald",
    title: "Compliance Reporting",
    points: ["TCEQ-approved laboratory", "ISO 17025:2017 accredited", "Standard Methods (APHA)", "Defensible chain-of-custody"],
  },
];

// Detailed pill-style tag list (rendered as a scannable cloud)
const TESTING_SERVICES = [
  { icon: Bug, label: "Culture Identifications to Genus Level" },
  { icon: FlaskConical, label: "Yeast and Mold Identification / Screens" },
  { icon: Microscope, label: "Bacterial Identifications / Screens" },
  { icon: Droplets, label: "Total Coliforms and E. coli" },
  { icon: Bug, label: "Enterococcus" },
  { icon: Microscope, label: "Fecal Coliform / Fecal Streptococcus" },
  { icon: TestTube, label: "Legionella" },
  { icon: Beaker, label: "Heterotrophic Plate Count" },
  { icon: Bug, label: "Pseudomonas Aeruginosa" },
  { icon: FlaskConical, label: "Iron and Sulfate Reducing Bacteria" },
  { icon: Microscope, label: "Gram Positive Organisms (Staph, Enterococcus, Strep)" },
  { icon: TestTube, label: "Mold Specific Quantitative PCR (MSQPCR)" },
];

const FULL_TEST_LIST = [
  "Drinking Water Coliform", "Legionella PCR", "Total Coliform by Enzyme Substrate",
  "Legionella Pneumophilia Serogroup 1", "E.coli by Enzyme Substrate",
  "Legionella Pneumophilia Serogroup 2-14", "Enterococcus by Enzyme Substrate",
  "Legionella Spp", "Total Coliform by Membrane Filtration", "Staphylococcus aureus",
  "E.coli by Membrane Filtration", "Rapid Fecal Coliform", "Fecal Coliform by Membrane Filtration",
  "Vibrio species", "Enterococcus by Membrane Filtration", "Yeast & Mold",
  "Fecal Streptococcus by Membrane Filtration", "Pseudomonas aeruginosa",
  "Geometric Mean Fecal Coliform", "Salmonella Spp", "Sewage Screen",
  "Microbiologically Influenced Corrosion (MIC)", "Total Coliform by Multiple Tube",
  "Acid Producing Bacteria", "E.coli by Multiple Tube", "Iron-Related Bacteria",
  "Fecal Coliform by Multiple Tube", "Sulfate-Reducing Bacteria", "Heterotrophic Plate Count",
  "Slime-Forming Bacteria", "Anaerobic Plate Count", "Heterotrophic Aerobic Bacteria",
  "Legionella CDC", "Denitrifying Bacteria", "Zone of Inhibition",
  "Nitrifying Bacteria", "Microscopic Exam",
];

const FORMS = [
  {
    name: "Bottle Request Form",
    desc: "Email orders@sterilemate.com to have sample bottles shipped to your facility",
    url:
      "mailto:orders@sterilemate.com" +
      "?subject=" + encodeURIComponent("Sample Bottle Request — A&B Labs / Sterile Mate") +
      "&body=" + encodeURIComponent(
        "Hi Sterile Mate orders team,\n\n" +
        "I'd like to request sample collection bottles for upcoming microbiology / water testing.\n\n" +
        "Facility / hospital name:\nShip-to address:\nContact name & phone:\nBottle types needed (e.g., Legionella, total coliform, heterotrophic plate count):\nQty per bottle type:\nTarget collection date:\nNotes / panel(s) you plan to run:\n\nThanks,\n"
      ),
    icon: FlaskConical,
  },
  { name: "Price Quote Form", desc: "Get pricing for any test panel in this catalog", url: "https://sterilemate.com/price-quote-form/", icon: FileText },
  { name: "Chain-of-Custody Form", desc: "Download the COC.xls for sample submission", url: "https://sterilemate.com/wp-content/uploads/2025/11/environmental-coc.xls", icon: Download },
  { name: "Accreditations & Certifications", desc: "ISO 17025:2017 · TCEQ · APHA Standard Methods", url: "https://sterilemate.com/accreditations/", icon: Award },
];

const TAT = [
  { days: "Standard", price: "Included", note: "7 business days" },
  { days: "4 Business Days", price: "+25%", note: "Quick-turn" },
  { days: "3 Business Days", price: "+50%", note: "Rush" },
  { days: "2 Business Days", price: "+75%", note: "Priority" },
  { days: "1 Business Day", price: "+100%", note: "STAT" },
];

// ============================================================================
// A&B LABS MICROBIOLOGY TESTING CATALOG — sourced from the A&B Labs price
// sheet. Each test is an Add-to-Cart product line item. The 4 per-job fees
// (FedEx, Environmental, Sustainability, Delivery) are applied automatically
// by QuoteCartContext when any product with source: "microbiology" is in cart.
// ============================================================================
const MICRO_TESTS = [
  { sku: "AB-PH",   name: "Corrosivity, pH",                method: "SW-846 9041A",       matrix: "Liquid", tat: "5 Days", price: 18.90,  icon: Atom,         accent: "cyan" },
  { sku: "AB-COND", name: "Conductivity",                   method: "SM 2510B",           matrix: "Liquid", tat: "5 Days", price: 27.00,  icon: Zap,          accent: "amber" },
  { sku: "AB-ALK",  name: "Alkalinity",                     method: "SM 2320B",           matrix: "Liquid", tat: "5 Days", price: 43.20,  icon: Beaker,       accent: "violet" },
  { sku: "AB-HARD", name: "Hardness",                       method: "SM 2340C",           matrix: "Liquid", tat: "5 Days", price: 54.00,  icon: Pipette,      accent: "emerald" },
  { sku: "AB-TOC",  name: "Total Organic Carbon (TOC)",     method: "SW-846 9060A",       matrix: "Liquid", tat: "5 Days", price: 54.00,  icon: Atom,         accent: "violet" },
  { sku: "AB-HPC",  name: "HPC @ 35°C for 48 hours ± 3 (PCA)", method: "SM 9215B",        matrix: "Liquid", tat: "5 Days", price: 43.20,  icon: Microscope,   accent: "emerald" },
  { sku: "AB-CL",   name: "Chloride",                       method: "EPA 300.0",          matrix: "Liquid", tat: "5 Days", price: 41.85,  icon: Droplets,     accent: "cyan" },
  { sku: "AB-MET",  name: "Fe, Cu, Mn (Metals)",            method: "EPA 200.7",          matrix: "Liquid", tat: "5 Days", price: 52.65,  icon: Atom,         accent: "amber" },
  { sku: "AB-LAL",  name: "Endotoxins (LAL) — Subcontracted", method: "Subcontracted",    matrix: "Liquid", tat: "Standard", price: 91.80, icon: TestTube,    accent: "violet" },
  { sku: "AB-LEGI", name: "Legionella",                     method: "PCR",                matrix: "Liquid", tat: "1 Business Day (STAT included)", price: 135.00, icon: Bug,          accent: "emerald" },
];

const MICRO_FEE_ROWS = [
  { label: "FedEx Charges (flat rate per cooler)",          amount: 75.0 },
  { label: "Environmental Fee (per Job ID)",                amount: 55.0 },
  { label: "Sustainability Fee (supplies & disposal)",      amount: 41.0 },
  { label: "Flat-rate sample delivery",                     amount: 48.0 },
];

const MICRO_DISCLAIMERS = [
  {
    title: "Turnaround Time (TAT)",
    body: "TAT is based on receipt time. Samples received before 3:00 PM are processed the same day; those received after are processed the next business day. Results available by 6:00 PM on the due date. Please confirm expedited turnaround requests prior to submitting samples to confirm lab capacity.\n\n5-7 Business Days = STANDARD\n4 Business Days = 25% surcharge\n3 Business Days = 50% surcharge\n2 Business Days = 75% surcharge\n1 Business Day = 100% surcharge",
  },
  {
    title: "Standing Fees & Minimums",
    body: "Sustainability Fee: $30.00 per job for supplies and disposal costs.\nMinimum Invoice Fee: $200.00 minimum invoice fee will be applied.\nSample Extract & Hold: fee applies to samples that are extracted and not analyzed.\nClient-specified MS / MSD: invoiced as an additional sample.\nField QC (Trip / Field / Rinsate / Equipment blanks): charged as an additional sample.\nStand-by fee / no samples to pick up: $50.00 flat fee.",
  },
  {
    title: "Weekend & Holiday Prep / Analysis",
    body: "Weekend Prep/Analysis Fee: $250.00 each day + analytical unit cost if sample requires set-up, a prep step, or final analysis on a weekend.\nHoliday Prep/Analysis Fee: $450.00 each day + analytical unit cost if sample requires set-up, a prep step, or final analysis on a Holiday or Holiday weekend. Fee applies unless prior arrangements are made with the lab.",
  },
  {
    title: "Shipping",
    body: "A&B Labs will pay for outgoing kit shipment costs if the kit order is received 3-5 business days in advance, using the most cost-efficient means available (UPS Ground, FedEx Ground, FedEx Express 3 Day, etc.). For international shipping, the client is responsible for both outbound and inbound shipping costs.\nSubcontracted samples: flat rate of $35.00 for delivery/shipping to another laboratory.",
  },
  {
    title: "Analysis & Reporting",
    body: "A Sample Receipt Confirmation is sent following login and review. Prices include standard analytical reporting (PDF + standard Excel). Level III, Level IV, or state-specific data deliverables incur an analytical surcharge. Additional EDDs available — contact your Project Manager. A&B Labs reserves the right to add charges beyond the initially quoted prices to recover the costs of additional sample cleanup, instrument cleanup, and/or instrument downtime caused by extremely contaminated matrices. Clients will be notified of these additional charges prior to submittal of the invoice. Field QA/QC samples (project-specific MS/MSDs, duplicates, equipment/rinsate blanks) are considered billable samples. A&B Labs reserves the right to charge for reanalysis costs if analysis confirms original result (within acceptable QA/QC RPD criteria).",
  },
  {
    title: "PLM Analysis",
    body: "A&B Labs analyzes all layers of wallboard/joint compounds, plasters, and roofing samples as required by NVLAP. The EPA and OSHA have specific requirements for reporting. The lab will charge per sample layer unless prior arrangements are made. If the chain-of-custody does not specify applicable testing standards, the final report will include a comment specifying which layer was identified.",
  },
  {
    title: "Quality Assurance / Accreditation",
    body: "A&B Labs uses approved analytical test methods documented in our Quality Assurance Manual (QAM) and Standard Operating Procedures (SOP). The lab is NELAP accredited for Environmental, AIHA for Industrial Hygiene, PJLA for Food and Petroleum analyses.",
  },
  {
    title: "Sample Media / Supplies & Storage",
    body: "Sampling containers, coolers, chain-of-custody forms, and packing material are included in the cost of analysis. Terracores, Encores, PVC filters, MCE filters, sorbent tubes are additional and included in the proposal. Other media, pumps and related equipment are available for additional charges.\nSamples are stored for 21 days from receipt unless prior arrangements are made. Samples placed on hold without further analysis incur a sample hold fee. Samples requiring additional preparation (filtering, digestion, crushing, drying, pulverizing, homogenizing, compositing) will incur additional fees.",
  },
  {
    title: "Invoicing",
    body: "Invoices reflect prices in the quotation set up by your sales/project manager. Prices are based on the sample quantities indicated and can change based on actual quantities submitted. Minimum invoice fee is $200.00. Payment terms are Net 30 days unless prior agreement is made. Payments accepted: credit card (Visa, Amex, MasterCard), debit card, check, or electronic transfer. A 3.5% convenience fee is applied to credit card payments (does NOT apply to ACH or check). Quote is valid for 90 days from issuance. Provide the Quote number on chains-of-custody to ensure proper invoicing.",
  },
];

// Tailwind-safe per-accent palette (static keys for purge).
const ACCENT = {
  cyan: { chip: "bg-cyan-500/15 text-cyan-200 ring-cyan-400/30", bar: "bg-cyan-500", text: "text-cyan-300" },
  violet: { chip: "bg-violet-500/15 text-violet-200 ring-violet-400/30", bar: "bg-violet-500", text: "text-violet-300" },
  amber: { chip: "bg-amber-500/15 text-amber-200 ring-amber-400/30", bar: "bg-amber-500", text: "text-amber-300" },
  emerald: { chip: "bg-emerald-500/15 text-emerald-200 ring-emerald-400/30", bar: "bg-emerald-500", text: "text-emerald-300" },
};

// Dark-on-electric-blue palette — chips read on glass-morphism cards over
// the page's electric-blue gradient. Kept as a separate map (rather than
// reusing ACCENT) so we can tune contrast independently for marketplace
// cards if needed.
const LIGHT_CHIP = {
  cyan: "bg-cyan-500/15 text-cyan-200 ring-1 ring-cyan-400/30",
  violet: "bg-violet-500/15 text-violet-200 ring-1 ring-violet-400/30",
  amber: "bg-amber-500/15 text-amber-200 ring-1 ring-amber-400/30",
  emerald: "bg-emerald-500/15 text-emerald-200 ring-1 ring-emerald-400/30",
};
const LIGHT_BAR = {
  cyan: "bg-cyan-400",
  violet: "bg-violet-400",
  amber: "bg-amber-400",
  emerald: "bg-emerald-400",
};

export default function MicrobiologyTestingPage() {
  const [showFull, setShowFull] = useState(false);
  const [openDisc, setOpenDisc] = useState(null);
  const { addItem, setQty, removeItem, setShowCart, cart } = useQuoteCart();
  const cartCount = cart.reduce((s, i) => s + (i.qty || 0), 0);

  const handleAddTest = (t) => {
    addItem({
      erp: t.sku,
      name: `${t.name} · ${t.method}`,
      source: "microbiology",
      category: "Microbiology Testing",
      unit_price: t.price,
      list_price: t.price,
      pack: t.matrix,
    });
  };

  // setQty inside the context clamps to a minimum of 1 — so going below 1
  // via the stepper would still leave one item in the cart. For the catalog
  // we want clicking "-" all the way to remove the item entirely, so we
  // route 0/negative values through `removeItem`.
  const decrementTest = (sku, currentQty) => {
    const next = currentQty - 1;
    if (next <= 0) removeItem(sku);
    else setQty(sku, next);
  };
  const incrementTest = (sku, currentQty) => setQty(sku, currentQty + 1);
  const setExactQty = (sku, value) => {
    const n = Math.max(0, parseInt(value || "0", 10));
    if (n === 0) removeItem(sku);
    else setQty(sku, n);
  };

  return (
    <div
      className="min-h-screen text-slate-100 bg-[#0a1733]"
      style={{
        backgroundImage:
          "radial-gradient(1200px 700px at 0% 0%, rgba(14,165,233,0.35), transparent 60%)," +
          "radial-gradient(1100px 600px at 100% 20%, rgba(37,99,235,0.30), transparent 55%)," +
          "radial-gradient(1000px 700px at 50% 100%, rgba(16,185,129,0.18), transparent 60%)," +
          "linear-gradient(180deg, #0a1733 0%, #0b2148 30%, #0c2d5c 65%, #0a1f44 100%)",
      }}
      data-testid="microbiology-testing-page"
    >
      <SiteHeader />

      {/* ───────── HERO ───────── */}
      <section className="relative overflow-hidden text-white">
        <div className="absolute inset-0 z-0">
          <img
            src={HERO_BG}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover opacity-30 mix-blend-luminosity"
          />
          {/* Electric-blue hero gradient — replaces the prior near-black navy */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#0ea5e9] via-[#2563eb] to-[#1e3a8a]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(16,185,129,0.35),transparent_60%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(56,189,248,0.30),transparent_55%)]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 py-14 md:py-24">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 mb-6 text-sm">
            <Link to="/" className="text-cyan-200/80 hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-4 h-4 text-cyan-400/50" />
            <Link to="/solutions" className="text-cyan-200/80 hover:text-white transition-colors">Solutions</Link>
            <ChevronRight className="w-4 h-4 text-cyan-400/50" />
            <span className="text-amber-300 font-semibold">Microbiology &amp; Water Testing</span>
          </div>

          <div className="grid lg:grid-cols-[1fr_440px] gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-emerald-500/15 ring-1 ring-emerald-400/30 backdrop-blur px-3 py-1.5 rounded-full mb-5">
                <Sparkles className="w-3.5 h-3.5 text-emerald-300" />
                <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-emerald-200">
                  Sterile Mate &times; A&amp;B Labs Partnership
                </span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-5 leading-[1.05] tracking-tight">
                Microbiology &amp; <span className="text-emerald-300">Water Testing</span><br />
                for the people who can&apos;t miss.
              </h1>

              <p className="text-lg md:text-xl text-slate-300 leading-relaxed mb-8 max-w-2xl">
                <strong className="text-white">ISO 17025:2017 accredited.</strong> TCEQ-approved.
                Defensible chain-of-custody on every sample. From SPD water and dialysis loops
                to Legionella surveillance and post-construction bio-burden &mdash; we own the
                analytical workflow so your compliance team doesn&apos;t have to.
              </p>

              <div className="flex flex-wrap gap-3">
                <Button
                  asChild
                  size="lg"
                  className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold shadow-lg shadow-amber-500/30"
                  data-testid="mb-cta-quote"
                >
                  <a href="#catalog">
                    Build your Quote
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </a>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="border-cyan-400/30 bg-white/5 text-cyan-100 hover:bg-cyan-500/10 hover:text-white hover:border-cyan-400/60 font-semibold backdrop-blur"
                  data-testid="mb-cta-bottles"
                >
                  <a
                    href={
                      "mailto:orders@sterilemate.com" +
                      "?subject=" + encodeURIComponent("Sample Bottle Request — A&B Labs / Sterile Mate") +
                      "&body=" + encodeURIComponent(
                        "Hi Sterile Mate orders team,\n\n" +
                        "I'd like to request sample collection bottles for upcoming microbiology / water testing.\n\n" +
                        "Facility / hospital name:\nShip-to address:\nContact name & phone:\nBottle types needed (e.g., Legionella, total coliform, heterotrophic plate count):\nQty per bottle type:\nTarget collection date:\nNotes / panel(s) you plan to run:\n\nThanks,\n"
                      )
                    }
                  >
                    <FlaskConical className="w-4 h-4 mr-2" /> Request Sample Bottles
                  </a>
                </Button>
              </div>

              {/* Stat pills */}
              <div className="grid sm:grid-cols-3 gap-3 mt-10">
                <div className="bg-white/[0.04] backdrop-blur rounded-xl p-4 ring-1 ring-white/[0.08]">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/15 ring-1 ring-emerald-400/25 flex items-center justify-center">
                      <ShieldCheck className="w-4 h-4 text-emerald-300" />
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-emerald-300/80">Accreditation</p>
                      <p className="text-sm font-bold text-white">ISO 17025 · TCEQ</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white/[0.04] backdrop-blur rounded-xl p-4 ring-1 ring-white/[0.08]">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-cyan-500/15 ring-1 ring-cyan-400/25 flex items-center justify-center">
                      <Clock className="w-4 h-4 text-cyan-300" />
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-cyan-300/80">Turnaround</p>
                      <p className="text-sm font-bold text-white">1-7 business days</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white/[0.04] backdrop-blur rounded-xl p-4 ring-1 ring-white/[0.08]">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-500/15 ring-1 ring-amber-400/25 flex items-center justify-center">
                      <TestTube className="w-4 h-4 text-amber-300" />
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-amber-300/80">Test catalog</p>
                      <p className="text-sm font-bold text-white">35+ panels</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Hero photo card */}
            <div className="hidden lg:block relative">
              <div className="absolute -inset-6 bg-gradient-to-br from-emerald-500/20 via-cyan-500/10 to-transparent blur-2xl rounded-3xl" />
              <div className="relative rounded-2xl overflow-hidden ring-1 ring-white/10 shadow-2xl shadow-emerald-900/40">
                <img
                  src={HERO_CARD}
                  alt="Gloved hands holding a petri dish with live bacterial colonies — typical microbiology QC workflow"
                  className="w-full h-[440px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0c1939] via-[#0c1939]/40 to-transparent" />
                <div className="absolute left-5 right-5 bottom-5">
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-300 mb-1">
                    Trusted by SPD, Dialysis, and EHS teams
                  </p>
                  <p className="text-sm text-slate-200 leading-snug">
                    Every sample arrives with a defensible chain-of-custody — ready for
                    Joint Commission, AAMI, and CDC surveys.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="relative z-10 h-1 bg-gradient-to-r from-transparent via-amber-400/60 to-transparent" />
      </section>

      {/* ───────── ACCREDITATION RIBBON ───────── */}
      <section className="bg-gradient-to-r from-emerald-500/[0.06] via-emerald-500/[0.08] to-cyan-500/[0.06] ring-1 ring-emerald-400/15">
        <div className="max-w-7xl mx-auto px-4 py-6 flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-sm">
          <div className="flex items-center gap-2 text-emerald-200">
            <ShieldCheck className="w-4 h-4" />
            <span className="font-semibold text-white">ISO/IEC 17025:2017</span>
            <span className="text-emerald-300/80">Accredited Laboratory</span>
          </div>
          <div className="hidden md:block h-4 w-px bg-emerald-400/25" />
          <div className="flex items-center gap-2 text-cyan-200">
            <Award className="w-4 h-4" />
            <span className="font-semibold text-white">TCEQ</span>
            <span className="text-cyan-300/80">Approved Texas Lab</span>
          </div>
          <div className="hidden md:block h-4 w-px bg-emerald-400/25" />
          <div className="flex items-center gap-2 text-amber-200">
            <FileText className="w-4 h-4" />
            <span className="font-semibold text-white">APHA Standard Methods</span>
            <span className="text-amber-300/80">For Water &amp; Wastewater</span>
          </div>
        </div>
      </section>

      {/* ───────── SERVICE PILLARS (4 columns) ───────── */}
      <section className="max-w-7xl mx-auto px-4 py-16 md:py-20">
        <div className="text-center mb-12 max-w-3xl mx-auto">
          <span className="inline-block text-[11px] font-bold uppercase tracking-[0.2em] text-emerald-200 bg-emerald-500/15 ring-1 ring-emerald-400/30 px-3 py-1.5 rounded-full mb-4">
            What we test
          </span>
          <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
            The full <span className="text-emerald-300">analytical workflow</span><br className="hidden md:inline" />
            on one slip.
          </h2>
          <p className="text-lg text-slate-300 leading-relaxed">
            Whether you need a single Legionella PCR or a multi-month dialysis-loop surveillance
            program, every panel is run, reported, and archived to the same defensible standard.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {SERVICE_PILLARS.map((p) => (
            <div
              key={p.title}
              className="group relative bg-white/[0.04] backdrop-blur rounded-2xl ring-1 ring-white/10 hover:ring-emerald-400/40 hover:bg-white/[0.06] hover:shadow-xl hover:shadow-emerald-900/30 transition-all overflow-hidden"
              data-testid={`mb-pillar-${p.accent}`}
            >
              <div className={`h-1.5 w-full ${LIGHT_BAR[p.accent]}`} />
              <div className="p-6">
                <div className={`w-12 h-12 rounded-xl ${LIGHT_CHIP[p.accent]} flex items-center justify-center mb-4`}>
                  <p.icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-white mb-3 leading-tight">{p.title}</h3>
                <ul className="space-y-2">
                  {p.points.map((point) => (
                    <li key={point} className="flex items-start gap-2 text-[13px] text-slate-300 leading-relaxed">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300 shrink-0 mt-0.5" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ───────── PARTNERSHIP BANNER ───────── */}
      <section className="relative max-w-7xl mx-auto px-4 pb-16 md:pb-20">
        <div className="relative overflow-hidden rounded-3xl ring-1 ring-white/10">
          <img
            src={PARTNERSHIP_PHOTO}
            alt="Modern microbiology laboratory bench with analytical equipment"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0c1939] via-[#0c1939]/85 to-[#0c1939]/20" />
          <div className="relative p-8 md:p-12 max-w-2xl">
            <span className="inline-block text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-300 bg-emerald-500/15 ring-1 ring-emerald-400/30 px-2.5 py-1 rounded-full mb-4">
              Sterile Mate × A&amp;B Labs
            </span>
            <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-3 leading-tight">
              One clinical partner. Every microbiology question your facility throws at it.
            </h2>
            <p className="text-slate-300 leading-relaxed">
              Our A&amp;B Labs partnership means every test you order through Sterile Mate is run
              by certified analysts following Standard Methods (APHA) &mdash; with the same
              chain-of-custody discipline whether it&apos;s a single Legionella culture or
              an ongoing dialysis-loop surveillance contract.
            </p>
          </div>
        </div>
      </section>

      {/* ───────── DETAILED TEST CATALOG (collapsible) ───────── */}
      <section className="max-w-7xl mx-auto px-4 pb-16 md:pb-20">
        <div className="bg-white/[0.04] backdrop-blur rounded-2xl ring-1 ring-white/10 shadow-lg shadow-slate-950/20 p-7 md:p-9">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-xl bg-emerald-500/15 ring-1 ring-emerald-400/30 flex items-center justify-center">
              <FlaskConical className="w-5 h-5 text-emerald-300" />
            </div>
            <div>
              <h2 className="text-2xl font-extrabold text-white leading-tight">Most-Ordered Panels</h2>
              <p className="text-sm text-slate-400">A quick scan of what hospitals run most. Need a panel that isn&apos;t listed? Email us.</p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {TESTING_SERVICES.map((s, i) => (
              <div
                key={s.label}
                className="flex items-center gap-3 p-3.5 bg-white/[0.03] hover:bg-emerald-500/10 ring-1 ring-white/10 hover:ring-emerald-400/40 rounded-xl transition-all"
                style={{ animationDelay: `${i * 0.04}s` }}
              >
                <div className="w-9 h-9 rounded-lg bg-emerald-500/15 ring-1 ring-emerald-400/30 flex items-center justify-center shrink-0">
                  <s.icon className="w-4 h-4 text-emerald-300" />
                </div>
                <span className="text-sm font-medium text-slate-200">{s.label}</span>
              </div>
            ))}
          </div>

          <div className="mt-6 flex justify-center">
            <button
              type="button"
              onClick={() => setShowFull((v) => !v)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-emerald-200 bg-emerald-500/15 ring-1 ring-emerald-400/40 hover:bg-emerald-500/25 hover:text-white transition-all"
              data-testid="toggle-full-list"
            >
              <TestTube className="w-4 h-4" />
              {showFull ? "Hide full test list" : `View full test list (${FULL_TEST_LIST.length}+ panels)`}
              <ChevronRight className={`w-4 h-4 transition-transform ${showFull ? "rotate-90" : ""}`} />
            </button>
          </div>

          {showFull && (
            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-2 pt-6 border-t border-white/10" data-testid="full-test-list">
              {FULL_TEST_LIST.map((t) => (
                <div key={t} className="flex items-center gap-2 text-sm text-slate-200 py-1">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300 shrink-0" />
                  <span>{t}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ───────── TURNAROUND TIME LADDER + FORMS GRID ───────── */}
      <section className="max-w-7xl mx-auto px-4 pb-16 md:pb-20">
        <div className="grid lg:grid-cols-2 gap-6">
          {/* TAT */}
          <div className="bg-white/[0.04] backdrop-blur rounded-2xl ring-1 ring-white/10 shadow-lg shadow-slate-950/20 p-7">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 rounded-xl bg-amber-500/15 ring-1 ring-amber-400/30 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-300" />
              </div>
              <div>
                <h3 className="text-xl font-extrabold text-white leading-tight">Turnaround Time</h3>
                <p className="text-sm text-slate-400">Standard 7 business days. STAT options when you need them.</p>
              </div>
            </div>
            <ul className="divide-y divide-white/10">
              {TAT.map((t, i) => (
                <li key={t.days} className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-4 h-4 text-amber-300" />
                    <span className="text-sm font-semibold text-white">{t.days}</span>
                    <span className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">{t.note}</span>
                  </div>
                  <span className={`text-sm font-bold ${i === 0 ? "text-emerald-300" : "text-amber-300"}`}>
                    {t.price}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Forms */}
          <div className="bg-white/[0.04] backdrop-blur rounded-2xl ring-1 ring-white/10 shadow-lg shadow-slate-950/20 p-7">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 rounded-xl bg-sky-500/15 ring-1 ring-sky-400/30 flex items-center justify-center">
                <FileText className="w-5 h-5 text-sky-300" />
              </div>
              <div>
                <h3 className="text-xl font-extrabold text-white leading-tight">Forms &amp; Resources</h3>
                <p className="text-sm text-slate-400">Everything you need to submit a sample today.</p>
              </div>
            </div>
            <div className="space-y-3">
              {FORMS.map((f, i) => (
                <a
                  key={f.name}
                  href={f.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-3 p-3.5 bg-white/[0.03] hover:bg-sky-500/10 ring-1 ring-white/10 hover:ring-sky-400/40 rounded-xl transition-all"
                  data-testid={`mb-form-${i}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-sky-500/15 ring-1 ring-sky-400/30 flex items-center justify-center shrink-0">
                    <f.icon className="w-4 h-4 text-sky-300" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-white group-hover:text-sky-200 transition-colors">{f.name}</p>
                    <p className="text-[12px] text-slate-400">{f.desc}</p>
                  </div>
                  <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-sky-200 transition-colors" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ───────── A&B LABS TESTING MARKETPLACE ───────── */}
      <section id="catalog" className="text-slate-100 py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10 max-w-3xl mx-auto">
            <span className="inline-block text-[11px] font-bold uppercase tracking-[0.2em] text-emerald-200 bg-emerald-500/15 ring-1 ring-emerald-400/30 px-3 py-1.5 rounded-full mb-4">
              Add tests to your Quote Cart
            </span>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
              A&amp;B Labs <span className="text-emerald-300">Testing Catalog</span>
            </h2>
            <p className="text-lg text-slate-300 leading-relaxed">
              Build your quote like any other Sterile Mate order. Per-job lab fees
              (FedEx · Environmental · Sustainability · Delivery) and your
              chosen turnaround surcharge roll in automatically at checkout.
              <span className="block text-sm mt-2 text-slate-400 italic">No 20% freight markup applies to microbiology testing — A&amp;B Labs delivery is already included in the per-job fees.</span>
            </p>
            {cartCount > 0 && (
              <Button
                onClick={() => setShowCart(true)}
                className="mt-5 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold shadow-lg shadow-amber-500/30"
                data-testid="mb-view-cart"
              >
                <ShoppingCart className="w-4 h-4 mr-2" /> View Quote Cart ({cartCount})
              </Button>
            )}
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {MICRO_TESTS.map((t) => {
              const Icon = t.icon;
              const inCartLine = cart.find((i) => i.erp === t.sku);
              const qty = inCartLine?.qty || 0;
              const lineTotal = qty * t.price;
              return (
                <div
                  key={t.sku}
                  className={`group relative bg-white/[0.04] backdrop-blur rounded-2xl ring-1 ${qty > 0 ? "ring-2 ring-emerald-400/70 shadow-xl shadow-emerald-500/20 bg-emerald-500/[0.08]" : "ring-white/10 hover:ring-emerald-400/40 hover:bg-white/[0.06] hover:shadow-lg hover:shadow-slate-950/30"} transition-all overflow-hidden flex flex-col`}
                  data-testid={`micro-card-${t.sku}`}
                >
                  <div className={`h-1.5 w-full ${LIGHT_BAR[t.accent]}`} />
                  <div className="p-5 flex flex-col flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <div className={`w-12 h-12 rounded-xl ${LIGHT_CHIP[t.accent]} flex items-center justify-center`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <div className="text-right">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">SKU</p>
                        <p className="text-xs font-mono text-slate-300">{t.sku}</p>
                      </div>
                    </div>

                    <h3 className="text-base font-extrabold text-white leading-snug mb-2 min-h-[2.5em]">
                      {t.name}
                    </h3>

                    <div className="grid grid-cols-2 gap-2 mb-4 text-[11px]">
                      <div>
                        <p className="font-bold uppercase tracking-wider text-slate-400 mb-0.5">Method</p>
                        <p className="text-slate-200">{t.method}</p>
                      </div>
                      <div>
                        <p className="font-bold uppercase tracking-wider text-slate-400 mb-0.5">Matrix</p>
                        <p className="text-slate-200">{t.matrix}</p>
                      </div>
                      <div className="col-span-2">
                        <p className="font-bold uppercase tracking-wider text-slate-400 mb-0.5">Turnaround</p>
                        <p className="text-slate-200">{t.tat}</p>
                      </div>
                    </div>

                    <div className="mt-auto pt-3 border-t border-white/10">
                      <div className="flex items-end justify-between mb-3">
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-300">Per sample</p>
                          <p className="text-xl font-extrabold text-white">${t.price.toFixed(2)}</p>
                        </div>
                        {qty > 0 && (
                          <div className="text-right">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Line total</p>
                            <p className="text-lg font-extrabold text-emerald-300">${lineTotal.toFixed(2)}</p>
                          </div>
                        )}
                      </div>

                      {qty === 0 ? (
                        <Button
                          size="sm"
                          onClick={() => handleAddTest(t)}
                          className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold"
                          data-testid={`micro-add-${t.sku}`}
                        >
                          <Plus className="w-3.5 h-3.5 mr-1" /> Add to Quote
                        </Button>
                      ) : (
                        // Segmented pill stepper — visually one unit with vertical
                        // dividers between the −, qty, + segments. Matches the
                        // user-provided design reference, restyled for the dark
                        // electric-blue background.
                        <div className="inline-flex w-full items-stretch h-11 rounded-full bg-white/10 ring-1 ring-white/20 shadow-sm overflow-hidden divide-x divide-white/15" data-testid={`micro-stepper-${t.sku}`}>
                          <button
                            type="button"
                            onClick={() => decrementTest(t.sku, qty)}
                            className="flex-1 flex items-center justify-center text-slate-300 hover:text-white hover:bg-white/10 transition-colors"
                            data-testid={`micro-dec-${t.sku}`}
                            aria-label="Decrease quantity"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <input
                            type="number"
                            min={0}
                            value={qty}
                            onChange={(e) => setExactQty(t.sku, e.target.value)}
                            className="flex-1 min-w-0 text-center text-base font-extrabold text-white bg-transparent outline-none focus:bg-emerald-500/15"
                            data-testid={`micro-qty-${t.sku}`}
                            aria-label="Quantity"
                          />
                          <button
                            type="button"
                            onClick={() => incrementTest(t.sku, qty)}
                            className="flex-1 flex items-center justify-center text-emerald-300 hover:text-white hover:bg-emerald-500/20 transition-colors"
                            data-testid={`micro-inc-${t.sku}`}
                            aria-label="Increase quantity"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Per-job fees reminder */}
          <div className="mt-6 rounded-2xl bg-emerald-500/[0.08] backdrop-blur ring-1 ring-emerald-400/30 p-5 md:p-6">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-9 h-9 rounded-lg bg-emerald-500/15 ring-1 ring-emerald-400/30 flex items-center justify-center shrink-0">
                <FlaskConical className="w-4 h-4 text-emerald-300" />
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-200">Applied automatically at checkout</p>
                <h3 className="text-lg font-extrabold text-white">Per-Job Lab Charges</h3>
                <p className="text-sm text-slate-300 mt-1">These fees are added on top of the test subtotal — same as the A&amp;B Labs price sheet. <strong className="text-white">No 20% freight markup on microbiology orders.</strong></p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {MICRO_FEE_ROWS.map((f) => (
                <div key={f.label} className="bg-white/[0.06] backdrop-blur ring-1 ring-white/10 rounded-xl p-3 shadow-sm">
                  <p className="text-[11px] text-slate-300 leading-snug mb-1">{f.label}</p>
                  <p className="text-base font-extrabold text-white">${f.amount.toFixed(2)}</p>
                </div>
              ))}
              <div className="bg-amber-500/10 ring-1 ring-amber-400/30 rounded-xl p-3 sm:col-span-2 lg:col-span-4">
                <p className="text-[11px] text-amber-200 leading-snug mb-1">$200 minimum invoice — if the test subtotal + TAT surcharge + per-job fees fall below $200, a top-up is added so the invoice meets the A&amp;B Labs minimum.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────── DISCLAIMERS (collapsible accordion) ───────── */}
      <section className="max-w-7xl mx-auto px-4 pb-16 md:pb-20">
        <div className="bg-white/[0.04] backdrop-blur rounded-2xl ring-1 ring-white/10 shadow-lg shadow-slate-950/20 p-7 md:p-9">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-11 h-11 rounded-xl bg-amber-500/15 ring-1 ring-amber-400/30 flex items-center justify-center">
              <FileText className="w-5 h-5 text-amber-300" />
            </div>
            <div>
              <h2 className="text-2xl font-extrabold text-white leading-tight">Terms, Disclaimers &amp; Policies</h2>
              <p className="text-sm text-slate-400">Click each section to expand. All policies sourced from the A&amp;B Labs price sheet.</p>
            </div>
          </div>
          <div className="space-y-2">
            {MICRO_DISCLAIMERS.map((d, i) => {
              const open = openDisc === i;
              return (
                <div key={d.title} className="rounded-xl bg-white/[0.03] ring-1 ring-white/10 hover:ring-amber-400/40 transition-all overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setOpenDisc(open ? null : i)}
                    className="w-full flex items-center justify-between gap-3 px-5 py-3.5 text-left"
                    data-testid={`disc-toggle-${i}`}
                  >
                    <span className="text-sm font-bold text-white">{d.title}</span>
                    <ChevronRight className={`w-4 h-4 text-amber-300 transition-transform ${open ? "rotate-90" : ""}`} />
                  </button>
                  {open && (
                    <div className="px-5 pb-4 text-sm text-slate-300 leading-relaxed whitespace-pre-line border-t border-white/10 pt-3 bg-white/[0.02]">
                      {d.body}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ───────── CONTACT & LOCATION ───────── */}
      <section className="max-w-7xl mx-auto px-4 pb-16 md:pb-20">
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="bg-white/[0.04] backdrop-blur rounded-2xl ring-1 ring-white/10 shadow-lg shadow-slate-950/20 p-7">
            <Phone className="w-6 h-6 text-sky-300 mb-3" />
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-sky-200 mb-1">Call</p>
            <a href="tel:8009883892" className="text-2xl font-extrabold text-white hover:text-sky-200 transition-colors">
              800.988.3892
            </a>
            <p className="text-xs text-slate-400 mt-2">Mon–Fri 7am–6pm CT · Sat 10am–2pm (sample receiving)</p>
          </div>
          <div className="bg-white/[0.04] backdrop-blur rounded-2xl ring-1 ring-white/10 shadow-lg shadow-slate-950/20 p-7">
            <Mail className="w-6 h-6 text-amber-300 mb-3" />
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-200 mb-1">Email</p>
            <a href="mailto:orders@sterilemate.com" className="text-base font-bold text-white hover:text-amber-200 transition-colors break-all">
              orders@sterilemate.com
            </a>
            <p className="text-xs text-slate-400 mt-2">Ask about custom panels, multi-month surveillance, courier pickup, or rush logistics.</p>
          </div>
          <div className="bg-white/[0.04] backdrop-blur rounded-2xl ring-1 ring-white/10 shadow-lg shadow-slate-950/20 p-7">
            <MapPin className="w-6 h-6 text-emerald-300 mb-3" />
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-200 mb-1">Lab &amp; pickup</p>
            <p className="text-sm font-bold text-white leading-snug">
              A&amp;B Labs<br />
              10100 East Freeway, Suite 100<br />
              Houston, TX 77029
            </p>
            <p className="text-xs text-slate-400 mt-2">Sample courier service in Houston · Baton Rouge · Tempe.</p>
          </div>
        </div>
      </section>

      {/* ───────── FINAL CTA ───────── */}
      <section className="relative overflow-hidden text-white">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0ea5e9] via-[#2563eb] to-[#1e40af]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(16,185,129,0.30),transparent_60%)]" />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-4 py-16 md:py-20 text-center">
          <div className="inline-flex items-center gap-2 bg-white/15 ring-1 ring-white/30 backdrop-blur px-3 py-1.5 rounded-full mb-5">
            <Sparkles className="w-3.5 h-3.5 text-amber-200" />
            <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-white">
              Defensible. Auditable. Fast.
            </span>
          </div>
          <h2 className="text-3xl md:text-5xl font-extrabold mb-4 leading-tight">
            Get a quote in <span className="text-amber-300">5 minutes</span>.
          </h2>
          <p className="text-sky-100 mb-8 text-lg max-w-2xl mx-auto leading-relaxed">
            Send us the panel and the matrix &mdash; we&apos;ll come back with pricing,
            sample-bottle counts, and the turnaround that fits your survey deadline.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Button
              asChild
              size="lg"
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold shadow-xl shadow-amber-500/30"
              data-testid="mb-final-quote"
            >
              <a href="#catalog">
                Build your Quote
                <ArrowRight className="w-4 h-4 ml-2" />
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-white/40 bg-white/10 text-white hover:bg-white/20 hover:text-white hover:border-white/60 font-semibold backdrop-blur"
              data-testid="mb-final-call"
            >
              <a href="tel:8009883892">
                <Phone className="w-4 h-4 mr-2" /> 800.988.3892
              </a>
            </Button>
          </div>
          <div className="mt-6">
            <Link
              to="/solutions#testing"
              className="inline-flex items-center gap-1.5 text-sm text-sky-100/80 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to Solutions
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
